/**
 * Vuex Store for the filter builder
 * @author      Jeff Adams <jeff@tinyfly.com>
 * @copyright   Traxo, Inc. 2018
 */

import isEmpty from 'lodash/isEmpty';
import { isStorageAvailable, deepCopy } from '@/utils/helpers.js';

const SESSION_KEY = 'filterQuery';

export const state = defaultStateFactory;

export const getters = {
  filterIndexById: (state) => (id) => {
    return state.filters.findIndex((filter) => filter.id === id);
  },

  /**
   * Return all filters that have model data
   * @param {Object} state - current state
   * @returns {Array}
   */
  appliedFilters: (state) => {
    return state.filters.filter((filter) => {
      return filter.persistent || !isEmpty(filter.model);
    });
  },

  /**
   * Return all available but empty filters
   * @param {Object} state - current state
   * @returns {Array}
   */
  availableFilters: (state) => {
    return state.filters.filter((filter) => {
      return isEmpty(filter.model) && !filter.persistent;
    });
  },

  /**
   * Format the applied filters with just key/value pairs appropriate for building a summary sentence
   * @param {Object} state - Vuex current state
   * @param {Object} getters - other Vuex getters for this module
   * @returns {Object}
   */
  appliedFiltersForSummary: (state, getters) => {
    let filters = {};
    getters.appliedFilters.forEach((filter) => {
      // explode any params that are arrays into separate keys
      if (Array.isArray(filter.queryParam)) {
        filter.queryParam.forEach((param) => {
          // don't include empty values
          if (filter.model[param]) {
            filters[param] = filter.model[param];
          }
        });
      } else {
        // make comma separated values into a proper array
        filters[filter.queryParam] = filter.model.split(',');
      }
    });
    return filters;
  },
};

export const mutations = {
  /**
   * Load the initial state of the filters based on the filter schema passed in
   * @param {Object} state
   * @param {Object} filterSchema
   * @returns {Undefined}
   */
  LOAD_FILTERS(state, filterSchema = []) {
    state.filters = deepCopy(filterSchema);
  },

  /**
   * Store the filter model data based on the filter query
   * @param {Object} state
   * @param {Object} query - query params from the selected filters
   * @returns {Undefined}
   */
  SET_FILTERS(state, query) {
    let queryKeys = Object.keys(query);
    state.filters = state.filters.map((filter) => {
      // Some widgets can toggle between different query params
      // Find any applied ones and and set the current query params appropriately
      if (filter.queryParamModes) {
        Object.keys(filter.queryParamModes).forEach((mode) => {
          const modeParams = filter.queryParamModes[mode];
          if (Array.isArray(modeParams)) {
            const matchingParams = modeParams.filter((param) => queryKeys.includes(param));
            if (matchingParams.length) {
              filter.currentMode = mode;
              filter.queryParam = matchingParams;
            }
          }
        });
      }

      if (queryKeys.indexOf(filter.queryParam) !== -1) {
        filter.model = query[filter.queryParam];

        // special handling of array value types
      } else if (Array.isArray(filter.queryParam)) {
        filter.queryParam.forEach((key) => {
          if (queryKeys.indexOf(key) !== -1) {
            filter.model[key] = query[key];
          }
        });
      }

      return filter;
    });
  },

  UPDATE_FILTER_QUERY_PARAM(state, { queryParam, index }) {
    let filter = { ...state.filters[index], queryParam: queryParam };
    state.filters.splice(index, 1, filter);
  },

  /**
   * Clear a particular filter's model data. Resetting it to an empty version of the same type
   * @param {Object} state
   * @param {Number} filterId - index within the filter list
   * @returns {Undefined}
   */
  CLEAR_FILTERS(state, filterId) {
    const index = state.filters.findIndex((filter) => filter.id === filterId);
    let model = state.filters[index].model;
    if (Array.isArray(model)) {
      model = [];
    } else if (typeof model === 'string') {
      model = '';
    } else if (typeof model === 'object') {
      model = {};
    } else {
      model = null;
    }

    state.filters[index].model = model;
  },

  CACHE_QUERY(state, { name, query }) {
    let q = { ...query };
    delete q.next;
    delete q.previous;
    delete q.page;
    // don't store any Pendo related query strings
    // these are used to launch the walkthrough guides
    delete q.pendo;

    state.queries[name] = q;
  },

  RESTORE_QUERY(state, cache) {
    state.queries = { ...cache };
  },

  /**
   * Completely reset back to the default state
   * @param {Object} state
   * @param {Object} defaultState
   * @returns {Undefined}
   */
  RESET_STATE(state, defaultState) {
    let newState = defaultState();
    Object.keys(newState).forEach((key) => {
      state[key] = newState[key];
    });
  },
};

export const actions = {
  init({ commit }) {
    if (isStorageAvailable('sessionStorage')) {
      commit('RESTORE_QUERY', JSON.parse(sessionStorage.getItem(SESSION_KEY) || '{}'));
    }
  },

  /**
   * Stores the filter schema for a particular list and sets the
   * data for each filter based on the query string
   * @param {Object} context
   * @param {Function} context.commit
   * @param {Object} payload
   * @param {Object} payload.filterSchema
   * @param {Object} payload.query
   * @returns {Promise}
   */
  load({ commit }, { filterSchema, query = {} }) {
    commit(
      'LOAD_FILTERS',
      filterSchema.map((filter, index) => {
        return { ...filter, id: `${index}` };
      })
    );
    commit('SET_FILTERS', query);
  },

  updateFilterQueryParam({ commit, getters }, { filterId, queryParam }) {
    const index = getters.filterIndexById(filterId);
    commit('UPDATE_FILTER_QUERY_PARAM', { queryParam, index });
  },

  /**
   * Removes the data for a particular filter
   * @param {Object} context
   * @param {Function} context.commit
   * @param {Number} index
   * @returns {Promise}
   */
  destroy({ commit }, filterId) {
    commit('CLEAR_FILTERS', filterId);
  },

  /**
   * Cache the filter bar query
   * @param {Object} context
   * @param {Function} context.commit
   * @param {Object} payload
   * @param {String} payload.name - key for the query, typically the route name
   * @param {Object} payload.query - filter builder query
   * @returns {Undefined}
   */
  storeQuery({ commit, state }, payload) {
    commit('CACHE_QUERY', payload);

    if (isStorageAvailable('sessionStorage')) {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(state.queries));
    }
  },

  /**
   * Sets the state back to the default values
   * @param {Object} context
   * @param {Function} context.commit
   * @returns {Promise}
   */
  reset({ commit }) {
    commit('RESET_STATE', defaultStateFactory);

    if (isStorageAvailable('sessionStorage')) {
      sessionStorage.removeItem(SESSION_KEY);
    }
  },
};

/**
 * Factory function for creating the default state object
 * @returns {Object}
 */
function defaultStateFactory() {
  return {
    queries: {},
    filters: [],
  };
}
